/**
 * The x axis a chart spans, in days or in ISO weeks.
 *
 * Split out of `seriesHelpers`: choosing which days an axis covers — and which unmeasured ones
 * still have to appear, so the chart cannot draw a line across a period nobody measured — is a
 * separate job from shaping a series.
 */

const MS_PER_DAY = 24 * 60 * 60 * 1000;
/**
 * Gaps wider than this are left unfilled.
 *
 * Five years covers a cage that sat unmeasured across a whole production cycle and
 * still draws the idle stretch at its true width. Beyond that it is a bioscope
 * whose clock drifted, which does happen in the field and lands a row decades away
 * from the rest. Filling to it would put tens of thousands of empty categories on
 * the axis and hang the chart.
 *
 * It was one year, which left the two ends of a longer idle stretch (13 Jan one
 * year, 9 Sep the next) on adjacent categories with a line drawn between them.
 */
const MAX_FILLED_GAP_DAYS = 5 * 366;

const MS_PER_WEEK = 7 * MS_PER_DAY;
/** Same reasoning as `MAX_FILLED_GAP_DAYS`, five years' worth of weeks. */
const MAX_FILLED_GAP_WEEKS = 5 * 53;

/**
 * Unique days from `rows`, ascending.
 *
 * ISO `YYYY-MM-DD` strings sort correctly lexicographically, so a plain string
 * comparator is right here. This deliberately does not rely on the API returning
 * rows in order — the legacy `sortDays` helper in `OptoGraphs` did, because its
 * comparator (`(a, b) => a.day > b.day`) returns a boolean that always coerces
 * to 0, silently leaving the input order untouched.
 */
function uniqueSortedDays<TRow>(rows: TRow[], day: (row: TRow) => string): string[] {
    const unique = new Set<string>();

    for (const row of rows) {
        const value = day(row);
        if (value) {
            unique.add(value);
        }
    }

    return Array.from(unique).sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
}

/** UTC midnight for an ISO day, or `null` when the string is not a real date. */
function toDayTimestamp(day: string): number | null {
    const timestamp = Date.parse(`${day}T00:00:00Z`);
    return Number.isNaN(timestamp) ? null : timestamp;
}

function toIsoDay(timestamp: number): string {
    return new Date(timestamp).toISOString().slice(0, 10);
}

/**
 * Inserts the calendar days missing between consecutive entries of an ascending
 * day list.
 *
 * A stretch of days with no measurements has to exist on the axis, otherwise the
 * two days either side of it end up adjacent — the chart then draws a line
 * straight from one to the other, which reads as a measured trend across a period
 * that was never measured at all. With the days present, the join in
 * `joinSeriesToDays` fills them with `null` and Chart.js breaks the line there.
 *
 * Arithmetic is done at UTC midnight so a DST change cannot shift a day by an
 * hour and duplicate or skip one. Entries that are not parseable dates, and gaps
 * wider than `MAX_FILLED_GAP_DAYS`, are left as they are.
 */
function fillGaps(keys: string[], stepMs: number, maxSteps: number): string[] {
    const filled: string[] = [];

    for (let index = 0; index < keys.length; index++) {
        const key = keys[index];
        const nextKey = keys[index + 1];

        filled.push(key);

        if (nextKey === undefined) {
            continue;
        }

        const from = toDayTimestamp(key);
        const to = toDayTimestamp(nextKey);
        if (from === null || to === null || to - from > maxSteps * stepMs) {
            continue;
        }

        for (let timestamp = from + stepMs; timestamp < to; timestamp += stepMs) {
            filled.push(toIsoDay(timestamp));
        }
    }

    return filled;
}

export function fillDayGaps(days: string[]): string[] {
    return fillGaps(days, MS_PER_DAY, MAX_FILLED_GAP_DAYS);
}

/**
 * The same, one entry per missing week rather than per missing day.
 *
 * The keys are the Mondays the weeks start on, so stepping seven days at a time lands on the
 * next Monday. Filling by day instead is what put all seven of a week's dates on the axis,
 * each rendering the same `W32` label.
 */
export function fillWeekGaps(weeks: string[]): string[] {
    return fillGaps(weeks, MS_PER_WEEK, MAX_FILLED_GAP_WEEKS);
}

/**
 * Builds the chart's day axis: every calendar day from the first row's day to the
 * last one's, with no holes.
 *
 * The axis spans the days the data covers, so gaps inside it are shown but a
 * leading or trailing stretch with no rows is not: the rows alone do not say
 * whether the requested range extended past them. That matches what the legacy
 * charts did, and is why this takes rows rather than the selected date range.
 *
 * NOTE: whether an unmeasured stretch at either end of the selected range should
 * be drawn as empty — the way the Behavior charts do, since their backend pads the
 * whole requested range — is an open product question, not an oversight. Changing
 * it means threading the page's date-picker bounds down to here and accepting that
 * a wide range over a recently stocked cage renders mostly empty. Raise a ticket
 * and get a product decision before doing it.
 */
export function dayAxis<TRow>(rows: TRow[], day: (row: TRow) => string): string[] {
    return fillDayGaps(uniqueSortedDays(rows, day));
}

/**
 * The week axis: one entry per ISO week, keyed by the Monday it starts on.
 *
 * The counterpart of {@link dayAxis} for a series that is already bucketed by week. Running a
 * weekly series through `dayAxis` produces an entry for every calendar day between the first
 * Monday and the last, and since `xAxisKind: 'week'` renders each entry as its ISO week number,
 * one week showed up as seven identical `W32` ticks.
 *
 * Weeks with no data are still inserted, for the reason `fillDayGaps` exists: without them two
 * measured weeks a month apart are drawn adjacent, which reads as a continuous series.
 */
export function weekAxis<TRow>(rows: TRow[], week: (row: TRow) => string): string[] {
    return fillWeekGaps(uniqueSortedDays(rows, week));
}
